import { useEffect, useState } from 'react';

/**
 * The clinic directory as a form sees it.
 *
 * `unavailable` covers both a failed call and a list that came back empty, because the API
 * deliberately answers the same way to either: the directory belongs to another system, and "it did
 * not answer" is never to be shown as "there are no clinics". Every form reading this degrades its
 * dropdown to a text box in that state, and every endpoint checking a submitted name stands its check
 * down in the same one — so the two ends agree about what an empty list means.
 */
export type ClinicList = { state: 'loading' } | { state: 'ready'; names: string[] } | { state: 'unavailable' };

/**
 * Fetches the clinic names. A function rather than the `ApiClient` itself, because the signed-in
 * sections and the public forms read the same directory through endpoints with different
 * authorization — `/api/complimentary-care/clinics` and `/api/public/clinics`.
 */
export type ClinicLoader = (signal: AbortSignal) => Promise<string[]>;

/**
 * Loads the clinic directory once, as its own concern and with its own failure.
 *
 * Separate from whatever else a form fetches on mount, deliberately: an unreachable directory should
 * cost the page its dropdown, not the page. The form is still fillable and still sendable.
 *
 * `load` goes into the effect's dependencies, so pass one that keeps its identity across renders —
 * `useCallback` at the call site. An inline arrow re-fetches on every render.
 */
export function useClinicDirectory(load: ClinicLoader): ClinicList {
  const [clinics, setClinics] = useState<ClinicList>({ state: 'loading' });

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        const names = await load(controller.signal);

        if (!controller.signal.aborted) {
          setClinics(names.length > 0 ? { state: 'ready', names } : { state: 'unavailable' });
        }
      } catch {
        if (!controller.signal.aborted) {
          setClinics({ state: 'unavailable' });
        }
      }
    })();

    return () => controller.abort();
  }, [load]);

  return clinics;
}
